import { useState } from 'react';
import { useUiStore } from '../store';
import { STRINGS } from '../i18n';
import { ToolCard } from '../components/ToolCard';
import { FEATURED_SLUGS, TOOLS, getToolBySlug } from '../data/tools';
import { trackEvent } from '../lib/analytics';

const SUBSCRIBE_ENDPOINT =
  import.meta.env.VITE_EMAIL_SUBSCRIBE_URL ||
  'https://pocketledger-email.andyhan100044.workers.dev/api/email/subscribe';

export function HomePage() {
  const locale = useUiStore((s) => s.locale);
  const toggleLocale = useUiStore((s) => s.toggleLocale);
  const t = STRINGS[locale];

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const featured = FEATURED_SLUGS.map((slug) => getToolBySlug(slug)).filter(
    (tool): tool is NonNullable<typeof tool> => Boolean(tool)
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim().length === 0) return;
    if (submitting) return;

    setSubmitting(true);
    setError('');
    trackEvent('email_subscribe_submit', { locale });

    try {
      const res = await fetch(SUBSCRIBE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'hero' }),
      });
      if (res.ok) {
        setSubmitted(true);
        setEmail('');
      } else {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error || `Subscription failed (${res.status})`);
      }
    } catch (err) {
      // Network failure — still acknowledge locally so UX is graceful
      setSubmitted(true);
      setEmail('');
      // Log for debugging
      console.warn('[email-subscribe] network error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
            {t.tagline}
            <br />
            <span className="text-brand-600">{t.subtagline}</span>
          </h1>
          <p className="mt-6 text-base text-slate-500 sm:text-lg">
            {t.comingSoon}
          </p>
        </div>
      </section>

      {/* Featured tools */}
      <section className="border-t border-slate-200 bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-semibold text-slate-900">
            {t.featuredHeading}
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((tool) => (
              <ToolCard key={tool.slug} tool={tool} />
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-semibold text-slate-900">
            {t.whyHeading}
          </h2>
          <ul className="mt-6 space-y-3 text-base text-slate-700">
            <li className="flex items-start gap-3">
              <span className="mt-1 inline-block h-2 w-2 rounded-full bg-brand-600" aria-hidden="true" />
              <span>{t.valuePrivacy}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 inline-block h-2 w-2 rounded-full bg-brand-600" aria-hidden="true" />
              <span>{t.valueNoLogin}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 inline-block h-2 w-2 rounded-full bg-brand-600" aria-hidden="true" />
              <span>{t.valueOpen}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 inline-block h-2 w-2 rounded-full bg-brand-600" aria-hidden="true" />
              <span>{t.valueSolo}</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Email capture */}
      <section className="border-t border-slate-200 bg-white px-6 py-16">
        <div className="mx-auto max-w-xl text-center">
          <h2 className="text-2xl font-semibold text-slate-900">
            {t.notifyCta}
          </h2>
          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row">
            <label htmlFor="notify-email" className="sr-only">
              Email
            </label>
            <input
              id="notify-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.notifyPlaceholder}
              disabled={submitting}
              className="flex-1 rounded-md border border-slate-300 px-4 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
            >
              {submitting ? '...' : t.notifyCta}
            </button>
          </form>
          {submitted && (
            <p className="mt-4 text-sm text-safe font-medium">{t.notifyThanks}</p>
          )}
          {error && (
            <p className="mt-4 text-sm text-critical">{error}</p>
          )}
        </div>
      </section>

      {/* Tool directory - kept for SEO + future routes */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="sr-only">All tools</h2>
          <p className="text-sm text-slate-500">
            {TOOLS.length} tools planned across finance, life, work, office, privacy, and entrepreneur.
          </p>
        </div>
      </section>
    </main>
  );
}

export default HomePage;
